import { useMemo } from "react";
import { arrayComparer, useMessageToState } from "../../../hooks/useMessageToState";
import { usePhotoSort } from "../../../hooks/use-photo-sort";

function getPhotoSelection(editor: eXpress.core.IEditor): readonly eXpress.core.Photo[] {
	return editor.definition ? editor.assets.photoSelection : [];
}

/**
 * Hook that returns valid urls to show in the photo selection list.
 * This list must be updated when photos are uploaded/downloaded
 */
export const usePhotoSelection = () => {
	const compareFunction = usePhotoSelectionCompareFunction();
	const photos = useMessageToState({
		name: "usePhotoSelection",
		filterMessage: eXpress.core.isPhotoSelectionChanged,
		getState: getPhotoSelection,
		comparer: arrayComparer,
	});

	return useMemo(() => photos.orderBy(compareFunction), [photos, compareFunction]);
};

export const usePhotoSelectionCompareFunction = () => {
	const [{ sortBy, sortDirection }] = usePhotoSort();
	const isDescending = sortDirection === eXpress.SortDirection.Descending;

	switch (sortBy) {
		case eXpress.SortBy.DateTaken:
			return isDescending
				? eXpress.core.Photo.compareByDateTakenDescending
				: eXpress.core.Photo.compareByDateTakenAscending;
		case eXpress.SortBy.Name:
			return isDescending
				? eXpress.core.Photo.compareByNameDescending
				: eXpress.core.Photo.compareByNameAscending;
		case eXpress.SortBy.DateAdded:
			return isDescending
				? eXpress.core.Photo.compareByIndexAddedDescending
				: eXpress.core.Photo.compareByIndexAddedAscending;
		default:
			return isDescending
				? eXpress.core.Photo.compareByDateTakenDescending
				: eXpress.core.Photo.compareByDateTakenAscending;
	}
};
